<template>
  <el-tabs v-model="tabActive" class="mb-5">
    <el-tab-pane :label="myArticlesLabel" name="articles">
      <div v-if="articleList.length">
        <div v-for="article in articleList" :key="article.id" class="mb--xl">
          <dara-article-card :article="article" />
        </div>
      </div>
      <div v-else>
        <dara-p class="opacity--50 my--md">( {{ $t('no-articles') }} )</dara-p>
      </div>
      <dara-pagination
        :page.sync="articlesPagination.page"
        :total="articlesPagination.total"
        :page-size="articlesPagination.limit"
        @current-change="fetch" />
    </el-tab-pane>

    <el-tab-pane :label="myTracksLabel" name="tracks">
      <div v-if="trackList.length">
        <div v-for="track in trackList" :key="track.id" class="mb-4">
          <dara-track-card :track="track" />
        </div>
      </div>
      <div v-else>
        <dara-p class="opacity--50 my--md">( {{ $t('no-tracks') }} )</dara-p>
      </div>
      <dara-pagination
        :page.sync="tracksPagination.page"
        :total="tracksPagination.total"
        :page-size="tracksPagination.limit"
        @current-change="fetch" />
    </el-tab-pane>

    <el-tab-pane :label="myQuestionsLabel" name="questions">
      <div v-if="questionList.length">
        <div v-for="question in questionList" :key="question.id" class="mb-4">
          <dara-question-card :question="question" />
        </div>
      </div>
      <div v-else>
        <dara-p class="opacity--50 my--md">( {{ $t('no-questions') }} )</dara-p>
      </div>
      <dara-pagination
        :page.sync="questionsPagination.page"
        :total="questionsPagination.total"
        :page-size="questionsPagination.limit"
        @current-change="fetch" />
    </el-tab-pane>

    <el-tab-pane :label="followedUsersLabel" name="followed-users">
      <div v-if="followedUsers.length">
        <div v-for="followed in followedUsers" :key="followed.id" class="mb-4">
          <dara-user-card
            :user="followed"
            :best-writer-id="bestWriter ? bestWriter.id : ''"
            :top-writer-id="topWriter ? topWriter.id : ''"
            :top-reader-id="topReader ? topReader.id : ''"
            @setDeleteUser="setDeleteUser($event)" />
        </div>
        <dara-modal-delete
          v-if="showModal()"
          :content="$t('are-you-sure-you-want-to-delete-this-user')"
          @close="setDeleteUser(null)"
          @confirm="onDeleteUser()">
        </dara-modal-delete>
      </div>
      <div v-else>
        <dara-p class="opacity--50 my--md">( {{ $t('no-followed-users') }} )</dara-p>
      </div>
      <dara-pagination
        :page.sync="followedUsersPagination.page"
        :total="followedUsersPagination.total"
        :page-size="followedUsersPagination.limit"
        @current-change="fetch" />
    </el-tab-pane>

    <el-tab-pane :label="followedTracksLabel" name="followed-tracks">
      <div v-if="followedTracks.length">
        <div v-for="followed in followedTracks" :key="followed.id" class="mb-4">
          <dara-track-card :track="followed" />
        </div>
      </div>
      <div v-else>
        <dara-p class="opacity--50 my--md">( {{ $t('no-followed-tracks') }} )</dara-p>
      </div>
      <dara-pagination
        :page.sync="followedTracksPagination.page"
        :total="followedTracksPagination.total"
        :page-size="followedTracksPagination.limit"
        @current-change="fetch" />
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import { removeUser, fetchFollowedUsers, fetchBestWriters, fetchTopReaders, fetchTopWriters } from '@/modules/user/user.service';
import { fetchUserArticles } from '@/modules/article/article.service';
import { fetchUserTracks, fetchFollowedTracks } from '@/modules/track/track.service';
import { fetchUserQuestions } from '@/modules/question/question.service';
import { getters } from '@/modules/user/user.store';
import { translate } from '@/services/translate.service';
import { toastError } from '@/services/toastService';

import DaraArticleCard from '@/modules/article/components/ArticleCard.vue';
import DaraTrackCard from '@/modules/track/components/TrackCard.vue';
import DaraQuestionCard from '@/modules/question/components/QuestionCard.vue';
import DaraUserCard from '@/modules/user/components/UserCard.vue';

export default {
  name: 'DaraUserTabs',
  components: {
    DaraArticleCard,
    DaraTrackCard,
    DaraQuestionCard,
    DaraUserCard,
  },
  props: {
    user: {
      type: Object,
    },
  },
  data() {
    return {
      articleList: [],
      trackList: [],
      questionList: [],
      followedUsers: [],
      followedTracks: [],
      totalArticles: 0,
      totalTracks: 0,
      totalQuestions: 0,
      totalFollowedUsers: 0,
      totalFollowedTracks: 0,
      articlesPagination: {
        page: 1,
        limit: 5,
      },
      tracksPagination: {
        page: 1,
        limit: 5,
      },
      questionsPagination: {
        page: 1,
        limit: 5,
      },
      followedUsersPagination: {
        page: 1,
        limit: 5,
      },
      followedTracksPagination: {
        page: 1,
        limit: 5,
      },
      bestWriter: null,
      topWriter: null,
      topReader: null,
      deletedUser: null,
    };
  },
  computed: {
    myArticlesLabel() {
      return this.user.id === getters.getUser().id ? `${translate('my-articles')} (${this.totalArticles})` : `${translate('users-articles')} (${this.totalArticles})`;
    },
    myTracksLabel() {
      return this.user.id === getters.getUser().id ? `${translate('my-tracks')} (${this.totalTracks})` : `${translate('users-tracks')} (${this.totalTracks})`;
    },
    myQuestionsLabel() {
      return this.user.id === getters.getUser().id ? `${translate('my-questions')} (${this.totalQuestions})` : `${translate('users-questions')} (${this.totalQuestions})`;
    },
    followedUsersLabel() {
      return `${translate('followed-users')} (${this.totalFollowedUsers})`;
    },
    followedTracksLabel() {
      return `${translate('followed-tracks')} (${this.totalFollowedTracks})`;
    },
    tabActive: {
      get() {
        if (this.totalArticles > 0) {
          return 'articles';
        }
        if (this.totalTracks > 0) {
          return 'tracks';
        }
        if (this.totalQuestions > 0) {
          return 'questions';
        }
        if (this.totalFollowedUsers > 0) {
          return 'followed-users';
        }
        if (this.totalFollowedTracks > 0) {
          return 'followed-tracks';
        }
        return 'articles';
      },
      set() {},
    },
  },
  mounted() {
    this.fetch();
    this.fetchFeaturedUsers();
  },
  methods: {
    fetch() {
      this.fetchUserArticles();
      this.fetchUserTracks();
      this.fetchUserQuestions();
      this.fetchFollowedUsers();
      this.fetchFollowedTracks();
    },
    fetchFeaturedUsers() {
      this.fetchBestWriter();
      this.fetchTopWriter();
      this.fetchTopReader();
    },
    fetchUserArticles() {
      fetchUserArticles(this.user, this.articlesPagination)
        .then(data => {
          this.articleList = data.data;
          this.totalArticles = data.pagination.total;
          Object.assign(this.articlesPagination, data.pagination);
        })
        .catch(() => {
          this.articleList = [];
        });
    },
    fetchUserTracks() {
      fetchUserTracks(this.user, this.tracksPagination)
        .then(data => {
          this.trackList = data.data;
          this.totalTracks = data.pagination.total;
          Object.assign(this.tracksPagination, data.pagination);
        })
        .catch(() => {
          this.trackList = [];
        });
    },
    fetchUserQuestions() {
      fetchUserQuestions(this.user, this.questionsPagination)
        .then(data => {
          this.questionList = data.data;
          this.totalQuestions = data.pagination.total;
          Object.assign(this.questionsPagination, data.pagination);
        })
        .catch(() => {
          this.questionList = [];
        });
    },
    fetchFollowedTracks() {
      fetchFollowedTracks(this.user, this.followedTracksPagination)
        .then(({ data }) => {
          this.followedTracks = data.followedTracks;
          this.$set(this.followedTracksPagination, 'total', parseInt(data.totalFollowedTracks));
          this.totalFollowedTracks = data.totalFollowedTracks;
        })
        .catch(() => {
          this.followedTracks = [];
        });
    },
    fetchFollowedUsers() {
      fetchFollowedUsers(this.user, this.followedUsersPagination)
        .then(({ data }) => {
          this.followedUsers = data.followedUsers;
          this.$set(this.followedUsersPagination, 'total', parseInt(data.totalFollowedUsers));
          this.totalFollowedUsers = data.totalFollowedUsers;
        })
        .catch(() => {
          this.followedUsers = [];
        });
    },
    fetchBestWriter() {
      fetchBestWriters(1)
        .then(user => {
          if (user) {
            this.bestWriter = user;
          }
        })
        .catch(() => {
          toastError(`${translate('error-getting-best-writer')}`);
          this.bestWriter = null;
        });
    },
    fetchTopWriter() {
      fetchTopWriters(1)
        .then(user => {
          if (user) {
            this.topWriter = user;
          }
        })
        .catch(() => {
          toastError(`${translate('error-getting-top-writer')}`);
          this.topWriter = null;
        });
    },
    fetchTopReader() {
      fetchTopReaders(1)
        .then(user => {
          if (user) {
            this.topReader = user;
          }
        })
        .catch(() => {
          toastError(`${translate('error-getting-top-reader')}`);
          this.topReader = null;
        });
    },
    setDeleteUser(user) {
      this.deletedUser = user;
    },
    showModal() {
      return this.deletedUser;
    },
    onDeleteUser() {
      if (this.deletedUser) {
        removeUser(this.deletedUser).then(() => this.$router.go(0));
      }
    },
  },
};
</script>
